import clsx from "clsx";
import type { HealthStatus, OverallStatus } from "../types/api";
import { healthStyle, overallStyle } from "../lib/statusStyles";

type StatusKind = "overall" | "health";

interface StatusBadgeProps {
  status: OverallStatus | HealthStatus;
  /** Pick which enum domain to interpret `status` as. Default "overall". */
  kind?: StatusKind;
  /** Visual size; default "md". */
  size?: "sm" | "md" | "lg";
  /** Override displayed label (e.g. show site key alongside default Korean label). */
  label?: string;
  /** Hide the small dot to the left of the label. */
  hideDot?: boolean;
  className?: string;
}

/**
 * Pill-shaped status badge with a colored dot + Korean label.
 *
 * - Color alone is never the sole signal (a11y) — the label is always rendered.
 * - Tailwind classes flow from `statusStyles.ts`; dark mode handled there too.
 * - Resolves unknown enum values gracefully to UNKNOWN tone (forward compatible).
 */
export default function StatusBadge({
  status,
  kind = "overall",
  size = "md",
  label,
  hideDot = false,
  className,
}: StatusBadgeProps) {
  const style =
    kind === "health"
      ? healthStyle(status as HealthStatus)
      : overallStyle(status as OverallStatus);

  const sizeClasses =
    size === "sm"
      ? "text-xs px-2 py-0.5 gap-1.5"
      : size === "lg"
        ? "text-sm px-3 py-1 gap-2"
        : "text-xs px-2.5 py-0.5 gap-2";

  const dotSize =
    size === "sm" ? "h-1.5 w-1.5" : size === "lg" ? "h-2.5 w-2.5" : "h-2 w-2";

  return (
    <span
      role="status"
      aria-label={`${label ?? style.label}`}
      className={clsx(
        "inline-flex items-center rounded-full border font-medium",
        style.bg,
        style.fg,
        style.border,
        sizeClasses,
        className,
      )}
    >
      {!hideDot && (
        <span
          aria-hidden="true"
          className={clsx("inline-block rounded-full", style.solid, dotSize)}
        />
      )}
      <span>{label ?? style.label}</span>
    </span>
  );
}
